import React, { ElementType, ReactElement, ReactNode } from 'react'
import { Button, Svg } from 'ui'
import ButtonArray from 'images/button-arrow.svg'
import { Link, navigate } from 'gatsby'
import { Flex, Text } from 'theme-ui'
import useCreateOrManageAccountUri from 'utils/useCreateOrManageAccountUri'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import useShouldRenderOnClient from 'utils/useShouldRenderOnClient'

export enum UseCaseItemSideType {
  IMAGE,
  TEXT,
}
export interface UseCasesContentType {
  icon: ElementType
  image: ElementType
  title: string
  description: ReactNode
  shortDescription: ReactNode
  buttonText: string
  linkTo: string
  leftSideType: UseCaseItemSideType
  animationDuration?: number
  shouldHideForSomeControl?: boolean
}

const UseCaseItem = ({
  icon,
  image,
  title,
  description,
  buttonText,
  linkTo,
  leftSideType,
}: UseCasesContentType): ReactElement => {
  const isMobile = useBreakpointIndex() === 0
  const createOrManageAccountUri = useCreateOrManageAccountUri()
  const shouldRenderOnClient = useShouldRenderOnClient()

  return (
    <Flex
      data-testid={title}
      sx={{
        backgroundColor: leftSideType === UseCaseItemSideType.IMAGE && 'darkItemBG50',
        boxShadow:
          leftSideType === UseCaseItemSideType.IMAGE
            ? ({ colors }): string => `0 1.6rem 2.4rem 0 ${colors?.darkItemBoxShadow}`
            : 'none',
        py: [leftSideType === UseCaseItemSideType.IMAGE && '2.4rem', '4.8rem'],
        borderRadius: '32px',
        mt: ['4rem', '4.8rem'],
        alignItems: 'center',
        flexDirection: ['column', 'column', 'row'],
        width: '100%',
        px: [0, 0, '4.8rem'],
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: ['center', 'center', 'flex-start'],
          width: ['100%', '62.4rem', '50%'],
          ml: [0, 0, leftSideType === UseCaseItemSideType.IMAGE && '4.8rem'],
          mr: [0, 0, leftSideType === UseCaseItemSideType.TEXT && '4.8rem'],
          order: [1, 1, +(leftSideType === UseCaseItemSideType.IMAGE)],
          textAlign: ['center', 'center', 'left'],
          px: [leftSideType === UseCaseItemSideType.IMAGE && '1.6rem', 0],
        }}
      >
        <Svg
          svg={icon}
          sx={{
            mb: ['1.6rem', '3.4rem'],
            width: ['4rem', '6.8rem'],
            height: ['4rem', '6.8rem'],
          }}
          descriptiveText={`${title} feature icon`}
        />
        <Text
          as="h3"
          sx={{
            fontSize: ['2.8rem', '4rem'],
            fontWeight: 'bold',
            mb: ['2.4rem', '3.2rem'],
          }}
        >
          {title}
        </Text>
        {isMobile && shouldRenderOnClient && (
          <Flex
            sx={{
              display: ['flex', 'none'],
              width: '100%',
              height: '25.6rem',
              mb: '2.4rem',
              justifyContent: 'center',
            }}
          >
            <Svg
              svg={image}
              sx={{
                flexShrink: 0,
                width: '25.6rem',
                height: '25.6rem',
              }}
              descriptiveText={`${title} feature illustration`}
            />
          </Flex>
        )}
        <Text
          sx={{
            fontSize: ['1.8rem', '2.1rem'],
            mb: ['2.4rem', '4.8rem'],
            color: 'aliceBlue60',
          }}
        >
          {description}
        </Text>
        <Flex
          sx={{
            width: ['100%', 'fit-content'],
            flexDirection: ['column', 'row', 'column'],
            alignItems: ['center', 'center', 'flex-start'],
          }}
        >
          <Button
            variant="secondary"
            data-testid="use-case-plans-button"
            ariaLabel="use case plans button"
            sx={{
              width: ['100%', 'fit-content'],
              backgroundColor: 'plum',
              px: ['1.8rem', '2.4rem'],
              py: ['0.8rem', '1.6rem'],
              borderRadius: '45px',
              border: 'none',
              mb: ['2.4rem', 0, '4.8rem'],
              mr: [0, '4.8rem', 0],
            }}
            onClick={() => navigate(createOrManageAccountUri)}
          >
            <Text
              sx={{
                fontSize: ['1.8rem', '2.1rem'],
                fontWeight: 'bold',
                color: 'white',
                mr: '0.8rem',
              }}
            >
              {buttonText}
            </Text>
            <Svg svg={ButtonArray} fill="white" descriptiveText="Button arrow" />
          </Button>
          <Link to={linkTo} style={{ textDecoration: 'none' }}>
            <Text
              sx={{
                fontSize: ['1.8rem', '2.1rem'],
                color: 'aliceBlue60',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                pl: [0, 0, '2.4rem'],
                ':hover': {
                  color: 'white',
                  '& svg path': {
                    fill: 'white',
                  },
                },
              }}
            >
              Learn more{' '}
              <Svg
                svg={ButtonArray}
                sx={{ ml: '0.8rem' }}
                fill="aliceBlue60"
                descriptiveText="Learn more arrow"
              />
            </Text>
          </Link>
        </Flex>
      </Flex>
      {!isMobile && shouldRenderOnClient && (
        <Flex
          sx={{
            display: ['none', 'flex'],
            width: ['100%', '100%', '50%'],
            height: ['25.6rem', '40rem', '46.8rem'],
            mb: ['2.4rem', '3.2rem', 0],
            justifyContent: 'center',
          }}
        >
          <Svg
            svg={image}
            sx={{
              flexShrink: 0,
              width: ['25.6rem', '40rem', '46.8rem'],
              height: ['25.6rem', '40rem', '46.8rem'],
            }}
            descriptiveText={`${title} feature illustration`}
          />
        </Flex>
      )}
    </Flex>
  )
}
export default UseCaseItem
